'use client';

/**
 * The shopper's own sentence in the conversation, with each link they pasted
 * drawn as a pill where they put it:
 *
 *     Want to have something like [ ViewSonic PX700HDH Projector ]
 *
 * The search bar leaves a short placeholder (`‹ViewSonic PX700HDH Projector›`,
 * shared/src/utils/link-tokens.ts) where the address was; it is paired with
 * the message's link attachments by label. The pill shows the same short
 * name, takes the page's own title once its preview is known and the address
 * alone gave no name, and a click brings the link's card into view.
 *
 * A placeholder with no link to answer to (hand-edited, or a link removed) is
 * left as the words it is.
 */

import React from 'react';
import { HiLink as LinkIcon } from 'react-icons/hi';
import { findLinkTokens, linkTokenLabel } from 'shared/src/utils/link-tokens';
import type { LinkPreview } from 'shared/src/types/link-preview.type';
import { useLinkPreview } from '@/lib/link-preview/use-link-preview';
import AmazonIcon from '@/public/icons/amazon-icon.svg';

const PILL_CLASS =
  'mx-0.5 inline-flex max-w-full items-center gap-1 rounded-full border border-zinc-300/80 bg-white/90 px-2 py-px align-baseline text-[0.9em] font-medium leading-snug text-zinc-700 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 dark:border-white/15 dark:bg-white/10 dark:text-zinc-100 dark:hover:bg-white/15';

/** A page title as short as a pill wants it: its first words. */
export function shortTitle(title: string, maxChars = 28, maxWords = 4): string {
  const words = title.replace(/\s+/g, ' ').trim().split(' ');
  let out = '';
  for (const word of words.slice(0, maxWords)) {
    const next = out ? `${out} ${word}` : word;
    if (out && next.length > maxChars) break;
    out = next;
  }
  return out.length > maxChars ? `${out.slice(0, maxChars - 1)}…` : out;
}

export function InlineLinkPillView({
  url,
  host,
  label,
  preview,
  onShow,
}: {
  url: string;
  host: string;
  label: string;
  preview: LinkPreview | null;
  onShow?: () => void;
}) {
  const title = preview && preview.kind !== 'unavailable' ? preview.title : null;
  // The address's own words name it; only a bare host is replaced by the page's title.
  const text = title && label === host ? shortTitle(title) : label;
  const isAmazon = preview?.kind === 'amazon' || /(^|\.)amazon\.|^a\.co$|^amzn\./.test(host);
  const inside = (
    <>
      {isAmazon ? (
        <AmazonIcon className="h-2.5 w-auto flex-shrink-0 fill-current" aria-hidden="true" />
      ) : (
        <LinkIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0 truncate">{text}</span>
    </>
  );
  // A button only where it can do something: no card to show, no button.
  return onShow ? (
    <button type="button" className={PILL_CLASS} title={`${title ?? label}\n${url}`} onClick={onShow} data-link-pill="">
      {inside}
    </button>
  ) : (
    <span className={PILL_CLASS.replace(/ hover:[^ ]+| focus-visible:[^ ]+/g, '')} title={`${title ?? label}\n${url}`} data-link-pill="">
      {inside}
    </span>
  );
}

/** Bring the link's card into view and mark it for a moment. */
function showCardOf(url: string): void {
  const card = Array.from(document.querySelectorAll<HTMLElement>('[data-link-card]')).find(
    (el) => el.dataset.linkUrl === url,
  );
  if (!card) return;
  const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  card.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'nearest' });
  card.classList.add('ring-2', 'ring-zinc-400/60');
  window.setTimeout(() => card.classList.remove('ring-2', 'ring-zinc-400/60'), 1200);
}

function InlineLinkPill({
  url,
  host,
  label,
  interactive,
}: {
  url: string;
  host: string;
  label: string;
  interactive: boolean;
}) {
  const { preview } = useLinkPreview(url);
  return (
    <InlineLinkPillView
      url={url}
      host={host}
      label={label}
      preview={preview}
      onShow={interactive ? () => showCardOf(url) : undefined}
    />
  );
}

export function UserTextWithLinks({
  text,
  links,
  interactive = true,
}: {
  text: string;
  links: ReadonlyArray<{ url: string; host: string }>;
  /** A click on a pill shows the link's card: only where the card is on screen. */
  interactive?: boolean;
}) {
  const labels = links.map((link) => linkTokenLabel(link.url));
  const ranges = findLinkTokens(text, labels).filter((r) => r.linkIndex !== null);
  if (ranges.length === 0) return <>{text}</>;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((range, i) => {
    if (range.start > cursor) nodes.push(text.slice(cursor, range.start));
    const link = links[range.linkIndex as number];
    nodes.push(
      <InlineLinkPill key={`${range.start}-${i}`} url={link.url} host={link.host} label={range.label} interactive={interactive} />,
    );
    cursor = range.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
}
